/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Zoom,
} from '@material-ui/core';

import GreyOutlinedButton from '../../../elements/GreyOutlinedButton';
import { useStyles } from './style';
import Settings from '../../../conf/settings';
import CustomIcons from '../../../icons/CustomIcons';
import DarkTooltip from '../../../elements/DarkTooltip';

interface CurlDialogProps {
  curlOpen: boolean;
  base64Encoded: string;
  externalIDPToken: string;
  tokenEndpoint?: string;
  onClose: () => void;
}

function CurlDialog(curlOpenProps: CurlDialogProps) {
  const classes = useStyles();
  const intl = useIntl();
  const { curlOpen, base64Encoded, externalIDPToken, tokenEndpoint, onClose } = curlOpenProps;

  const [showReal, setShowReal] = useState(false);
  const [copiedTooltipVisible, setCopiedTooltipVisible] = useState(false);

  const getCurlDialogCloseBtnText = () => {
    return intl.formatMessage({
      id: 'application.productionKeys.oauth.curlGeneration.closeButton.text',
      defaultMessage: 'Close',
    });
  };

  const applyReal = () => {
    setShowReal(!showReal);
  };

  const handleCopyClick = () => {
    let copyText = '';
    if (externalIDPToken == '') {
      copyText =
        `curl -k -X POST ${tokenEndpoint} -d ` +
        '"grant_type=client_credentials" -H ' +
        `"Authorization: Basic ${base64Encoded}"`;
    } else {
      copyText =
        `curl -k -X POST ${Settings.tokenEndpoint} -d ` +
        '"grant_type=urn:ietf:params:oauth:grant-type:token-exchange" -H ' +
        `"Authorization: Basic ${base64Encoded}" -H "Content-Type: application/x-www-form-urlencoded" -d ` +
        `"subject_token=${externalIDPToken}" -d ` +
        `"subject_token_type=urn:ietf:params:oauth:token-type:jwt" -d ` +
        `"requested_token_type=urn:ietf:params:oauth:token-type:jwt"`;
    }

    navigator.clipboard.writeText(copyText);
    setCopiedTooltipVisible(true);
    setTimeout(() => {
      setCopiedTooltipVisible(false);
    }, 2000);
  };

  const curlContent = () => {
    if (externalIDPToken == '') {
      return (
        <>
          <div>
            <span className={classes.command}> curl -k -X POST </span>
            {' ' + tokenEndpoint}
            <span className={classes.command}> -d </span>{' '}
            {'"grant_type=client_credentials"'}
          </div>
          <div>
            <span className={classes.command}> -H </span>
            {'"Authorization: Basic'}
            <a onClick={applyReal} className={classes.encodeVisible}>
              {showReal
                ? ' ' + base64Encoded
                : ' Base64(consumer-key:consumer-secret)'}
            </a>
            {'"'}
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            <span className={classes.command}> curl -k -X POST </span>
            {' ' + Settings.tokenEndpoint}
            <span className={classes.command}> -d </span>{' '}
            {'"grant_type=urn:ietf:params:oauth:grant-type:token-exchange"'}{' '}
            <span className={classes.command}> -d </span>{' '}
            {'"subject_token_type=urn:ietf:params:oauth:token-type:jwt"'}{' '}
            <span className={classes.command}> -d </span>{' '}
            {'"requested_token_type=urn:ietf:params:oauth:token-type:jwt"'}
            <span className={classes.command}> -d </span> {'"subject_token='}
            <a onClick={applyReal} className={classes.encodeVisible}>
              {showReal ? ' ' + externalIDPToken : ' idp-token'}
            </a>
            {'"'}
          </div>
          <div>
            <span className={classes.command}> -H </span>
            {'"Authorization: Basic'}
            <a onClick={applyReal} className={classes.encodeVisible}>
              {showReal
                ? ' ' + base64Encoded
                : ' Base64(consumer-key:consumer-secret)'}
            </a>
            {'"'}
          </div>
        </>
      );
    }
  };

  return (
    <Dialog
      open={curlOpen}
      classes={{ paper: classes.dialog }}
      onClose={onClose}
      aria-labelledby='alert-dialog-curl'
      aria-describedby='alert-dialog-curl-description'
    >
      <DialogTitle id='alert-dialog-curl' className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <FormattedMessage
            id='application.productionKeys.oAuth.token.curl.dialogTitle'
            defaultMessage='cURL to Generate Access Token'
          />
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id='alert-dialog-curl-description'>
          {externalIDPToken == '' && (
            <Typography className={classes.curlDescription}>
              <FormattedMessage
                id='application.productionKeys.oAuth.token.curl.description'
                defaultMessage='The following cURL command allows you to generate
                                 an access token using the client credentials grant type,'
              />
            </Typography>
          )}
          {externalIDPToken != '' && (
            <Typography className={classes.curlDescription}>
              <FormattedMessage
                id='application.productionKeys.oAuth.token.curl.description.tokenExchange'
                defaultMessage='The following cURL command shows how to generate an access token using the
                                            token exchange grant type'
              />
            </Typography>
          )}
          <div className={classes.contentWrapper}>
            <div
              className={classes.code}
              data-testid='curl-to-generate-access-token'
            >
              {curlContent()}
            </div>
            <div className={classes.copyButton}>
              <DarkTooltip
                title={'Copied'}
                TransitionComponent={Zoom}
                placement='right'
                open={copiedTooltipVisible}
              >
                <IconButton onClick={handleCopyClick}>
                  <CustomIcons icon={'copy-icon'} />
                </IconButton>
              </DarkTooltip>
            </div>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.action}>
        <GreyOutlinedButton
          className={classes.greyOutlinedButton}
          text={getCurlDialogCloseBtnText()}
          onClick={onClose}
        />
      </DialogActions>
    </Dialog>
  );
}

export default CurlDialog;
